import { all, put, takeEvery } from 'redux-saga/effects';
import { strings } from '../../common/i18n';
import httpActions from '../http/actions';
import { actionCreators } from './actions';
import { logToSentry } from '../../config/Sentry';

// A response body is only worth showing if it reads like an error message
// meant for a person. Anything else falls back to the generic string.
//
// This matters because the body is not always JSON: a Django 500 with DEBUG
// on returns its full HTML traceback page, and that was being toasted
// verbatim — hundreds of KB of markup rendered as one enormous Alert covering
// the screen. The traceback also embeds request headers, so the toast printed
// the user's bearer token and the API's client secret in plain sight.
const MAX_TOAST_MESSAGE_LENGTH = 200;

const displayableMessage = (message) => {
  if (typeof message !== 'string' || !message.trim()) return undefined;
  // Any markup document — a '<' start covers <!doctype, <html and bare tags.
  if (/^\s*</.test(message)) return undefined;
  // Truncate rather than discard. A long body is usually a real validation
  // error (DRF returns one line per field and clears 200 chars easily), and
  // replacing it with "unknown error" loses the only useful thing in it. The
  // markup guard above already handles the traceback page this cap was
  // originally written for.
  if (message.length > MAX_TOAST_MESSAGE_LENGTH) {
    return `${message.slice(0, MAX_TOAST_MESSAGE_LENGTH).trimEnd()}…`;
  }
  return message;
};

export function* toastHttpFailure(action) {
  const { error, payload } = action;

  // 401 responses are auth-layer errors (stale/missing token) — MSAL handles
  // token renewal, so there is no need to surface a toast to the user.
  if (payload?.status === 401) return;

  try {
    // error is the raw response body string; try to parse it first
    let message, name;
    if (typeof error === 'string') {
      try {
        const parsed = JSON.parse(error);
        message = parsed.message;
        name = parsed.name;
      } catch {
        message = error;
      }
    } else {
      ({ message, name } = error || {});
    }

    const safeMessage = displayableMessage(message);

    if (safeMessage) {
      yield put(
        actionCreators.error(name ? `${name}: ${safeMessage}` : safeMessage)
      );
    } else {
      yield put(actionCreators.error(strings('errors.unknown')));
    }
  } catch (err) {
    logToSentry("On toast HTTP failure:", { error: err });
    console.error(err);
  }
}

export default function* () {
  yield all([
    takeEvery(httpActions.type.HTTP_FAILURE, toastHttpFailure),
  ]);
}
